import { useFormSetValue } from '@framework/form/hooks/useFormSetValue.ts';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { type ILayerAttribute } from '../../metadata/LayerMetadata.ts';
import {
  diffLayerAttributes,
  type ILayerAttributeSnapshot,
  pruneLayerAttributes,
} from './reconcileLayerAttributes.ts';

const EMPTY_DIFF = diffLayerAttributes({}, new Set<string>());

/**
 * Confronta gli attributi configurati del layer (intersezione, identify,
 * competenza) con gli attributi disponibili dall'introspezione e permette
 * di rimuovere quelli non più disponibili.
 *
 * Essendo derivato dai valori del form e dall'introspezione, il confronto
 * vale sia al cambio URL sia all'apertura di una configurazione salvata.
 */
export function useLayerAttributeReconciliation(
  prefix: string,
  availableAttributes: ILayerAttribute[],
  introspectionOk: boolean,
) {
  const setValue = useFormSetValue();

  const intersectionAttributes = useWatch({
    name: `${prefix}intersection.attributes`,
  });
  const intersectionAliases = useWatch({
    name: `${prefix}intersection.aliases`,
  });
  const identifyAttributes = useWatch({
    name: `${prefix}identify.attributes`,
  });
  const identifyAliases = useWatch({ name: `${prefix}identify.aliases` });
  const competenceIdentifyingPropertyName = useWatch({
    name: `${prefix}competence.identifyingPropertyName`,
  });

  const snapshot: ILayerAttributeSnapshot = useMemo(
    () => ({
      intersectionAttributes,
      intersectionAliases,
      identifyAttributes,
      identifyAliases,
      competenceIdentifyingPropertyName,
    }),
    [
      intersectionAttributes,
      intersectionAliases,
      identifyAttributes,
      identifyAliases,
      competenceIdentifyingPropertyName,
    ],
  );

  const availableNames = useMemo(
    () => new Set(availableAttributes.map(attribute => attribute.name)),
    [availableAttributes],
  );

  const diff = useMemo(() => {
    // Mai confrontare contro un'introspezione fallita o vuota:
    // segnalerebbe come non validi tutti gli attributi configurati.
    if (!introspectionOk) return EMPTY_DIFF;
    return diffLayerAttributes(snapshot, availableNames);
  }, [introspectionOk, snapshot, availableNames]);

  const pruneInvalidAttributes = useCallback(() => {
    const patch = pruneLayerAttributes(snapshot, availableNames);

    if (patch.intersectionAttributes) {
      setValue(
        `${prefix}intersection.attributes`,
        patch.intersectionAttributes,
        { shouldDirty: true, shouldValidate: true },
      );
    }
    if (patch.intersectionAliases) {
      setValue(`${prefix}intersection.aliases`, patch.intersectionAliases, {
        shouldDirty: true,
      });
    }
    if (patch.identifyAttributes) {
      setValue(`${prefix}identify.attributes`, patch.identifyAttributes, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    if (patch.identifyAliases) {
      setValue(`${prefix}identify.aliases`, patch.identifyAliases, {
        shouldDirty: true,
      });
    }
    if (patch.competenceIdentifyingPropertyName === null) {
      setValue(`${prefix}competence.identifyingPropertyName`, null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [snapshot, availableNames, prefix, setValue]);

  return { diff, pruneInvalidAttributes };
}
